import { useMemo, useRef, useState } from 'react';

import { formatPhoneNumber } from '@fortress-technology-solutions/fortress-component-library/utils';

// eslint-disable-next-line max-len
import useApplicationStatusSingleSelect from '../../../containers/ApplicationProfile/LeaseApplicationTab/useApplicationStatusSingleSelect';
// eslint-disable-next-line max-len
import useAffordableWaitlistValidations from '../../../containers/ManageAffordableWaitlist/hooks.useAffordableWaitlistValidations';

import {
  getWaitlistStatusId,
  getStatusName,
  NEW_PORTAL_APP_STATUS,
} from '../../../containers/ApplicationProfile/LeaseApplicationTab';
import { showWaitlistUpdateFail } from '../../WaitlistInfoSection/utils';

import { formatNBedsArr } from '../../../utils/prospectPreferences-helpers';

import type { AddToWaitlistParams } from '../../../containers/ManageAffordableWaitlist/types';
import { useApplicationStatusOptions } from '../../../containers/ApplicationProfile/hooks';
import { APPLICATION_STATUS_IDS } from '../../../containers/ApplicationProfile/constants';

import leaseAppTabMessages from '../../../containers/ApplicationProfile/LeaseApplicationTab/messages';
import useIntl from '../../../hooks/useIntl';
import useWaitlistApplicationWindow from '../../../hooks/useWaitlistApplicationWindow';

const messages = {
  ...leaseAppTabMessages,
};

export default function useApplicationStatus({
  application,
  applicationStatusChangeHandler: applicationStatusChangeHandlerRaw,
  applicationStatuses,
  handleStatusChange,
  waitlistModalProps,
  applicationStatusUpdateAllowed,
  leaseExecuted,
  leaseSentToPortal,
  isResident,
  headOfHouseholdId,
  affordableWaitlistRecord,
  applicationStatusId,
  localWaitlistInfo,
  waitlistActions,
  refreshApplication,
}) {
  const intl = useIntl();
  const [pendingWaitlistV2Action, setPendingWaitlistV2Action] = useState(null);

  // A modal's success callback can fire from a stale render closure, so the
  // pending action is mirrored in a ref and read from there when deciding
  // whether an arriving success still belongs to the open action.
  const pendingWaitlistV2ActionRef = useRef(null);

  const startWaitlistV2Action = (action) => {
    pendingWaitlistV2ActionRef.current = action;
    setPendingWaitlistV2Action(action);
  };

  const clearWaitlistV2Action = () => {
    pendingWaitlistV2ActionRef.current = null;
    setPendingWaitlistV2Action(null);
  };
  // Defensive: default to no-op if not a function
  const applicationStatusChangeHandler =
    typeof applicationStatusChangeHandlerRaw === 'function'
      ? applicationStatusChangeHandlerRaw
      : async () => {};
  const {
    affordableWaitlistFlag,
    hasAffordableWaitlistPermissionAdd,
    hasAffordableWaitlistPermissionReinstate,
    accountThisPropertyForWaitlist,
    affordableWaitlistPropertySetting,
    isAffordableWaitlistPropertySettingStatusClosed,
    propertyAffordablePrograms,
  } = useAffordableWaitlistValidations();
  const { isEntryBlocked, isV2WaitlistEnabled } =
    useWaitlistApplicationWindow();
  const showAddToWaitlistModal =
    affordableWaitlistFlag &&
    accountThisPropertyForWaitlist &&
    affordableWaitlistPropertySetting &&
    hasAffordableWaitlistPermissionAdd;
  const showReinstateModal =
    affordableWaitlistRecord &&
    affordableWaitlistRecord.affordableWaitlistHistory?.length > 0;
  const affordableWaitlistEnabled =
    affordableWaitlistFlag &&
    accountThisPropertyForWaitlist &&
    affordableWaitlistPropertySetting;
  const disableStatusChange =
    !applicationStatusUpdateAllowed ||
    isResident ||
    leaseExecuted ||
    leaseSentToPortal;
  const waitlistStatusId = getWaitlistStatusId(applicationStatuses);
  const {
    id: applicationId,
    applicationStatus: { id: currentStatusId },
    applicants,
    prospectInfo: {
      prospectPreferences: { nBedsArr, nBeds, prefersUnitAccessible },
    },
  } = application;

  // Picking Waitlist is a V2 wizard route (the add and reinstate branches of
  // handleApplicationStatusChange) whenever V2 is enabled and the application
  // is in a state the wizard accepts. On that route the V2 gate decides the
  // option's state, so it matches what selecting it does. Every other route is
  // the legacy one, which the backend refuses on a closed waitlist whatever
  // its dates.
  const waitlistOpensV2Wizard =
    isV2WaitlistEnabled &&
    (affordableWaitlistRecord?.id
      ? Boolean(showReinstateModal)
      : getStatusName(applicationStatuses, currentStatusId) ===
        NEW_PORTAL_APP_STATUS);
  const isWaitlistEntryBlocked = waitlistOpensV2Wizard
    ? isEntryBlocked
    : isAffordableWaitlistPropertySettingStatusClosed;
  const disableWaitlistOption = waitlistOpensV2Wizard
    ? !hasAffordableWaitlistPermissionAdd || isEntryBlocked
    : affordableWaitlistFlag &&
      accountThisPropertyForWaitlist &&
      affordableWaitlistPropertySetting &&
      (!hasAffordableWaitlistPermissionAdd || isWaitlistEntryBlocked);

  const handleApplicationStatusChange = async (
    event,
    newStatusId,
    addToWaitlistParams,
    cancelApplicationParams,
    denyApplicationParams,
  ) => {
    if (isV2WaitlistEnabled && newStatusId !== currentStatusId) {
      if (
        newStatusId === APPLICATION_STATUS_IDS.canceled &&
        hasAffordableWaitlistPermissionAdd &&
        affordableWaitlistRecord?.id
      ) {
        startWaitlistV2Action('cancel');
        return;
      }
      if (
        newStatusId === APPLICATION_STATUS_IDS.denied &&
        hasAffordableWaitlistPermissionAdd &&
        affordableWaitlistRecord?.id
      ) {
        startWaitlistV2Action('deny');
        return;
      }
      if (
        newStatusId === waitlistStatusId &&
        hasAffordableWaitlistPermissionAdd &&
        addToWaitlistParams.showReinstateModal &&
        affordableWaitlistRecord?.id
      ) {
        startWaitlistV2Action('reinstate');
        return;
      }
      // Mirror image of reinstate, and the same rule the classic tab applies: a New Portal App
      // household has an Application and a converted prospect but has never had a waitlist
      // record. Gating on the status as well as the missing record keeps this condition
      // identical to the endpoint's guard, so a Denied or Canceled application with no record
      // stays on the legacy path rather than opening a wizard the backend would refuse.
      if (
        newStatusId === waitlistStatusId &&
        hasAffordableWaitlistPermissionAdd &&
        !affordableWaitlistRecord?.id &&
        getStatusName(applicationStatuses, currentStatusId) ===
          NEW_PORTAL_APP_STATUS
      ) {
        startWaitlistV2Action('add');
        return;
      }
    }
    try {
      await applicationStatusChangeHandler(
        currentStatusId,
        newStatusId,
        waitlistStatusId,
        localWaitlistInfo,
        waitlistActions,
        handleStatusChange,
        waitlistModalProps,
        addToWaitlistParams,
        cancelApplicationParams,
        denyApplicationParams,
        intl,
        affordableWaitlistEnabled,
      );
    } catch (err) {
      showWaitlistUpdateFail(intl, err);
    } finally {
    }
  };

  const permissionRequired =
    (waitlistOpensV2Wizard ||
      (affordableWaitlistFlag && accountThisPropertyForWaitlist)) &&
    (!applicationStatusUpdateAllowed || !hasAffordableWaitlistPermissionAdd);

  const waitlistIsClosed =
    (waitlistOpensV2Wizard ||
      (affordableWaitlistFlag && accountThisPropertyForWaitlist)) &&
    isWaitlistEntryBlocked;

  const waitlistTooltipText = permissionRequired
    ? intl.formatMessage(messages.permissionRequired)
    : waitlistIsClosed
    ? intl.formatMessage(messages.waitlistClosed)
    : '';

  const [applicationStatusOptions] = useApplicationStatusOptions(
    applicationStatuses,
    applicationStatusId,
    disableWaitlistOption,
    affordableWaitlistEnabled,
    waitlistTooltipText,
    hasAffordableWaitlistPermissionReinstate,
  );

  const hohApplicant = useMemo(() => {
    return applicants.length === 1
      ? applicants[0]
      : applicants.find(
          (applicant) =>
            applicant.affordableRelationshipId === headOfHouseholdId ||
            applicant.applicantType?.name === 'Financial Lease Signer',
        );
  }, [applicants, headOfHouseholdId]);

  const addToWaitlistParams: AddToWaitlistParams = {
    info: {
      applicant: hohApplicant?.name,
      phone: formatPhoneNumber(hohApplicant?.applicantCustomer?.phoneNumber),
      incomeLevel: 'Not Determined',
      householdSize: applicants.length,
      preferredBedroomSize: formatNBedsArr(nBedsArr, nBeds),
      accessibleUnit: prefersUnitAccessible ? 'Yes' : 'No',
    },
    affordablePrograms: propertyAffordablePrograms,
    showAddToWaitlistModal,
    showReinstateModal,
    applicationIdExt: applicationId,
    affWlRecord: affordableWaitlistRecord,
  };

  const cancelApplicationParams = {
    showCancelModal: showAddToWaitlistModal,
  };

  const denyApplicationParams = {
    showDenyModal: showAddToWaitlistModal,
  };

  const { ReactHookFormProps, formProps, setValue } =
    useApplicationStatusSingleSelect({
      handleApplicationStatusChange,
      statusUpdateAllowed: applicationStatusUpdateAllowed,
      disableStatusChange,
      disableWaitlistOption,
      addToWaitlistParams,
      applicationStatusOptions,
      applicationStatusId,
      currentStatusId,
      affordableWaitlistPropertySetting,
      cancelApplicationParams,
      denyApplicationParams,
      statusTracker: true,
    });

  addToWaitlistParams.setValue = setValue;

  const handleWaitlistV2ActionClose = () => {
    clearWaitlistV2Action();
    addToWaitlistParams.setValue('applicationStatusId', currentStatusId);
  };

  const handleWaitlistV2ActionSuccess = (result) => {
    // A dismissed modal keeps its mutation alive, so a success can arrive for an
    // action the user already replaced (deny submitted, dismissed, cancel
    // opened, deny resolves). Only accept the action that is still pending.
    if (result?.action !== pendingWaitlistV2ActionRef.current) return;

    clearWaitlistV2Action();

    // Cancel + "continue application" only ends the waitlist record; the
    // application deliberately stays in process, so leave its status alone and
    // put the dropdown back where it was.
    if (result.keptInProcess) {
      addToWaitlistParams.setValue('applicationStatusId', currentStatusId);
      return;
    }

    // The backend has already moved the application to In Process, and may
    // have changed its property class, so writing the status back from the
    // record held here would PUT a stale application. Reload it instead.
    if (result.action === 'add' && result.movedToInProcess) {
      addToWaitlistParams.setValue('applicationStatusId', currentStatusId);
      refreshApplication?.();
      return;
    }

    const newStatusIdByAction = {
      deny: APPLICATION_STATUS_IDS.denied,
      cancel: APPLICATION_STATUS_IDS.canceled,
      reinstate: waitlistStatusId,
      add: waitlistStatusId,
    };
    const newStatusId = newStatusIdByAction[result.action];
    addToWaitlistParams.setValue('applicationStatusId', newStatusId);
    handleStatusChange(null, newStatusId);
  };

  return {
    ReactHookFormProps,
    formProps,
    showAddToWaitlistModal,
    isV2WaitlistEnabled,
    pendingWaitlistV2Action,
    handleWaitlistV2ActionClose,
    handleWaitlistV2ActionSuccess,
  };
}
